/**
 * Cuadro de estado del edificio que va sobre el mapa de la lámina.
 *
 * Vive fuera del raster del mapa A PROPÓSITO. Si se dibujara junto con las
 * teselas quedaría horneado en la imagen, y moverlo obligaría a volver a
 * renderizar el mapa entero — imposible de arrastrar con fluidez. Dibujado como
 * capa encima, reposicionarlo cuesta un redibujo barato.
 *
 * Muestra el ESTADO REAL de cada nivel y nada más. Lo que hay que hacer es una
 * acción, y las acciones viven en las brechas de la cenefa: mezclarlas acá haría
 * que el mapa dijera "instalar" sobre un nivel donde todavía no hay nada.
 */

import {
  clientDeaCount,
  hasExternalDea,
  isPublicLevel,
  isSharedLevel,
  levelsBottomUp,
  sharedDeaNumber,
} from '../domain/compliance.js';
import { totalLevels } from '../domain/levels.js';
import { equipmentLabel, equipmentNumbers } from '../domain/numbering.js';
import { clipText } from './canvas-utils.js';

const ROW_H = 15;
const HEAD_H = 6 + 13 + 12;
const FOOT_H = 6;
const PAD_X = 8;

const COLORS = {
  cliente: '#3cb44b',
  externo: '#d9a441',
  nopub: '#5f7386',
};

/** Filas visibles del cuadro: sólo los niveles que tienen algo que declarar. */
export function buildingRows(building, numbering, buildingIndex) {
  return levelsBottomUp(building)
    .slice()
    .reverse()
    .map((level) => {
      if (clientDeaCount(building, level) > 0) {
        const numbers = equipmentNumbers(numbering, buildingIndex, level);
        const names = (building.pisoNombres || {})[level];
        let text = numbers.length ? equipmentLabel(numbers, names) : 'DEA instalado';
        if (isSharedLevel(building, level)) {
          const shared = sharedDeaNumber(building, level);
          text += shared ? ` · comparte D${shared}` : ' · de terreno';
        }
        return { level, kind: 'cliente', text };
      }
      if (hasExternalDea(building, level)) {
        return { level, kind: 'externo', text: 'DEA externo' };
      }
      if (!isPublicLevel(building, level)) {
        return { level, kind: 'nopub', text: 'sin uso público' };
      }
      return null;
    })
    .filter(Boolean);
}

export function buildingSubtitle(building) {
  return building.sub
    ? `${building.pisos} pisos + ${building.sub} subt. = ${totalLevels(building)} niveles`
    : `Edificio · ${totalLevels(building)} niveles`;
}

/**
 * Mide el cuadro a su escala natural.
 * El ancho es dinámico para que el nombre del edificio salga SIEMPRE completo:
 * un nombre recortado en un plano de entrega es un error de identificación.
 */
export function measureBuildingBox(ctx, building, rows) {
  const subtitle = buildingSubtitle(building);

  ctx.font = 'bold 11px Arial';
  let width = ctx.measureText(building.nombre).width;
  ctx.font = '8.5px Arial';
  width = Math.max(width, ctx.measureText(subtitle).width);
  ctx.font = 'bold 9.5px Arial';
  rows.forEach((row) => {
    width = Math.max(width, 34 + ctx.measureText(row.text).width);
  });

  return {
    width: width + PAD_X * 2,
    height: HEAD_H + rows.length * ROW_H + FOOT_H,
  };
}

/**
 * Dibuja el cuadro y la línea que lo une con el pictograma del edificio.
 *
 * @param {{x:number,y:number}} anchor Punto de enganche en la lámina.
 * @param {number} scale Escala del cuadro; el operador la ajusta en la previa.
 * @returns {{x:number,y:number,w:number,h:number}} Rectángulo dibujado, para hit testing.
 */
export function drawBuildingBox(ctx, building, rows, anchor, position, scale = 1) {
  const subtitle = buildingSubtitle(building);
  const natural = measureBuildingBox(ctx, building, rows);
  const w = natural.width * scale;
  const h = natural.height * scale;
  const x = position.x;
  const y = position.y;

  // Conector desde el pictograma hasta el borde más cercano del cuadro.
  const targetX = anchor.x < x ? x : x + w;
  const targetY = Math.min(Math.max(anchor.y, y + 8), y + h - 8);
  ctx.strokeStyle = 'rgba(255,255,255,.6)';
  ctx.lineWidth = 1.2;
  ctx.setLineDash([4, 3]);
  ctx.beginPath();
  ctx.moveTo(anchor.x, anchor.y);
  ctx.lineTo(targetX, targetY);
  ctx.stroke();
  ctx.setLineDash([]);

  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);

  const iw = natural.width;
  const ih = natural.height;

  ctx.fillStyle = 'rgba(16,26,36,.85)';
  ctx.strokeStyle = 'rgba(255,255,255,.45)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(0, 0, iw, ih, 5);
  ctx.fill();
  ctx.stroke();

  let textY = 6;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 11px Arial';
  ctx.fillText(building.nombre, PAD_X, textY);
  textY += 13;
  ctx.fillStyle = '#9fd3b3';
  ctx.font = '8.5px Arial';
  ctx.fillText(subtitle, PAD_X, textY);
  textY += 12;

  rows.forEach((row) => {
    ctx.beginPath();
    ctx.arc(PAD_X + 4, textY + 7, 3.5, 0, 2 * Math.PI);
    ctx.fillStyle = COLORS[row.kind];
    ctx.fill();

    ctx.fillStyle = '#8fa6ba';
    ctx.font = 'bold 8px Arial';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(`N${row.level}`, PAD_X + 11, textY + 7);

    ctx.fillStyle = row.kind === 'nopub' ? '#8296a6' : '#dfe6ea';
    ctx.font = 'bold 9.5px Arial';
    ctx.fillText(clipText(ctx, row.text, iw - PAD_X - 34), PAD_X + 34, textY + 7);
    ctx.textBaseline = 'top';
    textY += ROW_H;
  });

  ctx.restore();
  return { x, y, w, h };
}

/** Posición por defecto: a la derecha del pictograma, centrada verticalmente. */
export function defaultBoxPosition(anchor, size) {
  return { x: anchor.x + 18, y: anchor.y - size.height / 2 };
}
